/**
 * ticketflow/seedCatalog — catalogue de seeds EMBARQUÉ + enrichissable.
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * Le « magasin de seeds » vit DANS la build admin (donc présent dans `admin.exe`
 * ET sur ticket-admin.amia.fr qui sert la même SPA) — jamais côté serveur de site.
 *
 *  • Seeds EMBARQUÉS : auto-découverte des `agency-*.json` de `./seeds/` (webpack
 *    `require.context`) — déposer un fichier suffit à l'ajouter au catalogue livré.
 *  • Seeds AJOUTÉS par l'éditeur : importés (fichier) ou CAPTURÉS depuis un site, persistés
 *    SUR LA CONSOLE (`/api/console/magasin`) → le catalogue s'enrichit sans rebuild.
 *
 *    Ils vivaient dans le `localStorage` du navigateur. C'était un mauvais endroit pour un
 *    patrimoine : un nettoyage de cache, un autre poste, une navigation privée, et le magasin
 *    n'existait plus — alors qu'une capture est l'état d'un site à un instant donné, parfois
 *    le seul exemplaire d'une configuration livrée. Le `localStorage` ne sert plus que de
 *    CACHE hors-ligne, pour l'`admin.exe` qui tourne sans console centrale.
 *  • Chaque seed = arbre de config brut (format `agency-*.json`), prêt pour
 *    `PUT /api/config` (api.saveConfig) ET téléchargeable en `.json`.
 *
 * ⚠ CE DOSSIER N'EST PAS UN MIROIR de `apps/seed/data`. Le magasin s'ALIMENTE des mêmes jeux
 * de données, il n'en est pas la copie : un article peut porter des informations PROPRES à la
 * console — libellé, notes, applicabilité — que le serveur de site ne consomme pas. Il est
 * donc volontairement EXCLU de `scripts/sync-partages.mjs` : une synchronisation les
 * écraserait à chaque livraison, en silence. La relation est magasin ↔ console, et c'est la
 * console qui APPLIQUE un article sur son client — le serveur.
 */

const LS_KEY = 'ticketflow.admin.userSeeds';

/** Dérive les métadonnées d'affichage d'un arbre de config. */
export function deriveEntry(data, filename, builtin = false) {
  return {
    filename,
    data,
    builtin,
    name: data?.agency?.name || filename,
    nameAr: data?.agency?.nameAr || '',
    description: data?.agency?.companySloganFr || '',
    descriptionAr: data?.agency?.companySlogan || '',
    primaryColor: data?.ui?.primaryColor || '#607D8B',
    servicesCount: data?.serviceTypes?.length || 0,
    countersCount: data?.counters?.length || 0,
    services: (data?.serviceTypes || []).map((s) => ({
      id: s.id, name: s.name, nameAr: s.nameAr, nameEn: s.nameEn, color: s.color,
    })),
  };
}

/** Un objet ressemble-t-il à un seed d'agence valide ? */
export function isSeedShape(data) {
  return !!(data && typeof data === 'object' && data.agency && Array.isArray(data.serviceTypes));
}

// Seeds embarqués (livrés avec la build) --------------------------------------
// eslint-disable-next-line no-undef
const ctx = require.context('./seeds', false, /agency-.*\.json$/);
export const builtinSeeds = ctx
  .keys()
  .map((key) => deriveEntry(ctx(key), key.replace(/^\.\//, ''), true));

// Seeds ajoutés par l'utilisateur (persistés) ---------------------------------
export function loadUserSeeds() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr)
      ? arr.filter((e) => isSeedShape(e?.data)).map((e) => deriveEntry(e.data, e.filename, false))
      : [];
  } catch {
    return [];
  }
}

function persistUserSeeds(entries) {
  const slim = entries.map((e) => ({ filename: e.filename, data: e.data }));
  localStorage.setItem(LS_KEY, JSON.stringify(slim));
}

/** Ajoute (ou remplace, par nom de fichier) un seed utilisateur. Retourne la liste. */
export function addUserSeed(data, filename) {
  const name = String(filename || `agency-${(data?.agency?.name || 'seed').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}.json`);
  const current = loadUserSeeds().filter((e) => e.filename !== name);
  const next = [...current, deriveEntry(data, name, false)];
  persistUserSeeds(next);
  return next;
}

/** Retire un seed utilisateur par nom de fichier. Retourne la liste. */
export function removeUserSeed(filename) {
  const next = loadUserSeeds().filter((e) => e.filename !== filename);
  persistUserSeeds(next);
  return next;
}

// ─── Magasin porté par la CONSOLE ────────────────────────────────────────────
//
// Une seule règle : la console fait foi quand elle répond, le cache local prend le relais
// quand elle ne répond pas (`admin.exe` en LAN, console injoignable). On ne fait JAMAIS
// échouer une action du magasin parce que le réseau est absent — l'éditeur travaille
// parfois chez un client, sur une connexion qui n'est pas la sienne.

const MAGASIN = '/api/console/magasin';
const opts = { credentials: 'same-origin', headers: { 'Content-Type': 'application/json' } };

/** `null` si la console n'est pas joignable (mode local) — l'appelant retombe sur le cache. */
async function demanderMagasin(methode, corps, suffixe = '') {
  try {
    const r = await fetch(`${MAGASIN}${suffixe}`, {
      ...opts,
      method: methode,
      ...(corps === undefined ? {} : { body: JSON.stringify(corps) }),
    });
    if (!r.ok) return null;
    const j = await r.json();
    return Array.isArray(j?.data) ? j.data : null;
  } catch {
    return null; // pas de console : `admin.exe` en direct sur un site
  }
}

const enEntrees = (articles) => articles.map((a) => deriveEntry(a.data, a.filename, false));

/**
 * Charge le magasin. Au premier chargement d'une console qui n'en a pas encore, MIGRE ce
 * que le navigateur portait — sans quoi le magasin bâti au fil des mois disparaîtrait le
 * jour de la mise à jour, ce qui est exactement le défaut qu'on corrige.
 */
export async function chargerMagasin() {
  const distant = await demanderMagasin('GET');
  if (distant === null) return loadUserSeeds(); // console absente → cache local

  const local = loadUserSeeds();
  if (!distant.length && local.length) {
    const migre = await demanderMagasin('PUT', local.map((e) => ({ filename: e.filename, data: e.data })));
    if (migre) { persistUserSeeds(enEntrees(migre)); return enEntrees(migre); }
    return local;
  }
  const entrees = enEntrees(distant);
  persistUserSeeds(entrees); // miroir hors-ligne
  return entrees;
}

/** Ajoute un article au magasin de la console (repli : cache local). */
export async function ajouterArticle(data, filename) {
  const nom = String(filename || `agency-${(data?.agency?.name || 'seed').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}.json`);
  const distant = await demanderMagasin('POST', { filename: nom, data });
  if (distant === null) return addUserSeed(data, nom);
  const entrees = enEntrees(distant);
  persistUserSeeds(entrees);
  return entrees;
}

/**
 * Article COMPLET, médias inclus (base64) — nécessaire à l'APERÇU pour afficher le vrai
 * logo, et à la distribution. La liste ne porte que les noms et tailles des médias : les
 * charger tous à chaque affichage du catalogue coûterait plusieurs mégaoctets pour rien.
 */
export async function lireArticleComplet(filename) {
  try {
    const r = await fetch(`${MAGASIN}/${encodeURIComponent(filename)}`, { credentials: 'same-origin' });
    if (!r.ok) return null;
    return (await r.json())?.data || null;
  } catch {
    return null; // pas de console : l'aperçu se rabat sur la config seule, sans logo
  }
}

/** Retire un article du magasin de la console (repli : cache local). */
export async function retirerArticle(filename) {
  const distant = await demanderMagasin('DELETE', undefined, `/${encodeURIComponent(filename)}`);
  if (distant === null) return removeUserSeed(filename);
  const entrees = enEntrees(distant);
  persistUserSeeds(entrees);
  return entrees;
}

/** Catalogue complet = embarqués + utilisateur (dédoublonné par filename, tri FR). */
export function buildCatalog(userSeeds = loadUserSeeds()) {
  const seen = new Set();
  const all = [];
  for (const e of [...userSeeds, ...builtinSeeds]) {
    if (seen.has(e.filename)) continue;
    seen.add(e.filename);
    all.push(e);
  }
  return all.sort((a, b) => a.name.localeCompare(b.name, 'fr'));
}
